import "./main-app.css";
import { Header } from "../general/header/header";
import React, { useEffect, useState, useContext, useRef } from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import Sider from "../general/sider/sider";
import { PageNotFound } from "../components/404/404-page";
import { FrontOfficeDashboard } from "../components/front-office/dashboard/front-office-dashboard";
import { PaymentFacilitators } from "../components/back-office/payment-facilitators/payment-facilitators";
import { ChargeTypes } from "../components/back-office/charge-types/charge-types";
import { Participants } from "../components/back-office/participants/participants";
import { BackofficeTransactions } from "../components/back-office/transactions/transactions";
import { FrontOfficePaymentFacilitatorDisputes } from "../components/front-office/disputes/payment-facilitator-disputes";
import { FrontOfficeBankDisputes } from "../components/front-office/disputes/bank-disputes";
import { SuperAgentUsers } from "../components/back-office/super-agent-users/super-agent-users";
import { BulkSettlements } from "../components/back-office/bulk-settlements/bulk-settlements";
import { FrontOfficeBulkSettlement } from "../components/front-office/bulk-settlement/front-office-bulk-settlement";
import { FrontOfficeSuperAgentUsers } from "../components/front-office/super-agent-user/front-office-super-agent-users";
import { FrontOfficeTransactions } from "../components/front-office/transactions/front-office-transactions";
import { BackOfficeDashboard } from "../components/back-office/dashboard/back-office-dashboard";
import { PublicHolidays } from "../components/back-office/public-holidays/public-holidays";
import { Configurations } from "../components/back-office/configurations/configurations";
import { Home } from "../components/home/home";
import { ChangePassword } from "../shared/components/change-password/change-password";
import { ParticipantUsers } from "../components/back-office/participant-users/participant-users";
import { LOCAL_STORAGE_SERVICE } from "../core/services/storage-service";
import { ParticipantReports } from "../components/back-office/participant-reports/participant-reports";
import { BulkDisputes } from "../shared/components/bulk-disputes/bulk-disputes";
import { BackofficeTransactionReports } from "../components/back-office/transaction-reports/transaction-reports";
import { FrontofficeTransactionReports } from "../components/front-office/transaction-reports/transaction-reports";
import { UserManagement } from "../components/back-office/user-management/user-management";
import { BulkDisputeResponse } from "../components/front-office/bulk-dispute-response/bulk-dispute-response";
import { RequeryManagement } from "../components/back-office/requery-management/requery-management";
import { AllWorkflowRequests } from "../components/back-office/workflow/all-workflow-requests";
import { SelfWorkflowRequests } from "../components/back-office/workflow/self-workflow-requests";
import { UploadedReconciliationFiles } from "../components/back-office/reconciliation/statement-upload/uploaded-files";
import { ReconciliationDailyUploads } from "../components/back-office/reconciliation/daily-uploads/daily-upload";
import { CreditReconciliation } from "../components/back-office/reconciliation/credit-debit-reconciliation/credit-reconciliation";
import { DebitReconciliation } from "../components/back-office/reconciliation/credit-debit-reconciliation/debit-reconciliation";
import { MainContext } from "../../App";
import { ReconciliationConfigurations } from "../components/back-office/reconciliation/configurations/reconciliation-configurations";
import { SERVICES } from "../core/services/services";
import { TransitAccounts } from "../components/back-office/transit-account-setup/transit-accounts";
import { ManualDisputeRefunds } from "../components/back-office/manual-payments/manual-dispute-refunds";
import { ManualSettlementPayments } from "../components/back-office/manual-payments/manual-settlement-payments";
import { FrontOfficeMandates } from "../components/front-office/mandates/mandates";
import { BackOfficeMandates } from "../components/back-office/mandates/mandates";
import { MerchantCategories } from "../components/back-office/merchant-categories/merchant-categories";
import { DebitMandateConfigurations } from "../components/back-office/mandate-configurations/mandate-configurations";
import { VerificationLimits } from "../components/back-office/verification-limits/verification-limits";
import { FrontOfficeIntegrationDetails } from "../components/front-office/integration-details/integration-details";
import { FrontOfficeMandateConfigurations } from "../components/front-office/mandate-configurations/front-office-mandate-configurations";
import { FrontOfficeDisputes } from "../components/front-office/disputes/disputes";
import { BackofficeBankDisputes } from "../components/back-office/disputes/bank-disputes";
import { BackofficePaymentFacilitatorDisputes } from "../components/back-office/disputes/payment-facilitator-disputes";
import { DisputeDetailsPage } from "../components/back-office/disputes/dispute-details-page";
import { UserTypes } from "../shared/constants";
import { TransactionDetailsPage } from "../shared/components/transactions/transaction-details-page";
import { MandateDetails } from "app/shared/components/mandates/mandate-details";
import { MandateCancellationRequests } from "app/shared/components/mandate-cancellation-requests/mandate-cancellation-requests";

export const BACKOFFICE_SINGLE_DISPUTE_ROUTE = "/back-office/disputes";
export const FRONTOFFICE_SINGLE_DISPUTE_ROUTE = "/front-office/disputes";
export const BACKOFFICE_SINGLE_TRANSACTION_ROUTE = "/back-office/transaction";
export const FRONTOFFICE_SINGLE_TRANSACTION_ROUTE = "/front-office/transaction";

export const MainApp = () => {
  const [idleTimeInMinutes] = useState(5);
  const mainContext = useContext(MainContext);
  const activityHandlerTimeout = useRef(null);
  const logoutTimeout = useRef(null);
  let history = useHistory();

  useEffect(() => {
    function setLogoutTimeout() {
      logoutTimeout.current = setTimeout(() => {
        logout();
      }, 60 * 1000 * idleTimeInMinutes);
    }

    const activityHandler = () => {
      if (logoutTimeout.current) clearTimeout(logoutTimeout.current);
      if (activityHandlerTimeout.current)
        clearTimeout(activityHandlerTimeout.current);
      activityHandlerTimeout.current = setTimeout(() => {
        setLogoutTimeout();
      }, 5000);
    };

    window.addEventListener("mousemove", activityHandler);
    window.addEventListener("keydown", activityHandler);
    window.addEventListener("scroll", activityHandler);
    window.addEventListener("click", activityHandler);

    function logout() {
      SERVICES.LOGOUT().finally(() => {
        history.push("/login");
        mainContext.mainDispatch({ type: "LOG_OUT" });
        LOCAL_STORAGE_SERVICE.CLEAR_STORAGE();
        mainContext.mainDispatch({
          type: "SET_ALERT",
          alert: {
            title: "Info",
            description: "Your session has timed out, please login again.",
            type: "error",
            showAlert: true,
          },
        });
      });
    }

    setLogoutTimeout();

    return () => {
      if (activityHandlerTimeout.current)
        clearTimeout(activityHandlerTimeout.current);
      if (logoutTimeout.current) clearTimeout(logoutTimeout.current);
      window.removeEventListener("mousemove", activityHandler);
      window.removeEventListener("keydown", activityHandler);
      window.removeEventListener("scroll", activityHandler);
      window.removeEventListener("click", activityHandler);
    };
  }, [history, idleTimeInMinutes, mainContext]);

  return (
    <div>
      <div>
        <Sider />
      </div>
      <div id="main">
        <div>
          <Header />
        </div>
        <div className="main-body inner-content">
          <Switch>
            <Route exact path="/" component={Home} />
            <Route
              exact
              path="/front-office/dashboard"
              component={FrontOfficeDashboard}
            />
            <Route
              exact
              path="/back-office/dashboard"
              component={BackOfficeDashboard}
            />
            {/*<Route path="/dashboard/:id" component={FrontOfficeDashboard} />*/}
            {/* <Route exact path="/user-profile" component={UserProfile} /> */}
            <Route
              path="/back-office/transactions"
              component={BackofficeTransactions}
            />
            <Route
              path="/back-office/requery-management"
              component={RequeryManagement}
            />
            <Route
              path="/back-office/transaction-reports"
              component={BackofficeTransactionReports}
            />
            <Route
              path="/front-office/transaction-reports"
              component={FrontofficeTransactionReports}
            />
            <Route
              path="/front-office/transactions"
              component={FrontOfficeTransactions}
            />
            <Route
              path="/back-office/bulk-settlements"
              component={BulkSettlements}
            />
            <Route
              path="/front-office/bulk-settlements"
              component={FrontOfficeBulkSettlement}
            />
            <Route
              path="/front-office/payment-facilitator-users"
              component={FrontOfficeSuperAgentUsers}
            />
            <Route
              path="/back-office/payment-facilitators"
              component={PaymentFacilitators}
            />
            {/* <Route path="/back-office/disputes" component={BankDisputes} /> */}
            <Route
              path="/back-office/disputes/bank"
              component={BackofficeBankDisputes}
            />
            <Route
              path="/back-office/disputes/payment-facilitator"
              component={BackofficePaymentFacilitatorDisputes}
            />

            <Route
              path="/front-office/disputes/bank"
              component={FrontOfficeBankDisputes}
            />
            <Route
              path="/front-office/disputes/payment-facilitator"
              component={FrontOfficePaymentFacilitatorDisputes}
            />
            <Route
              path="/back-office/disputes/pending-manual-refunds"
              component={ManualDisputeRefunds}
            />
            <Route
              path="/back-office/disputes/pending-manual-settlements"
              component={ManualSettlementPayments}
            />
            <Route exact path={`${BACKOFFICE_SINGLE_DISPUTE_ROUTE}/:logCode`}>
              <DisputeDetailsPage userType={UserTypes.Backoffice} />
            </Route>
            <Route exact path={`${FRONTOFFICE_SINGLE_DISPUTE_ROUTE}/:logCode`}>
              <DisputeDetailsPage userType={UserTypes.Frontoffice} />
            </Route>
            <Route
              exact
              path={`${BACKOFFICE_SINGLE_TRANSACTION_ROUTE}/:transactionReference`}
            >
              <TransactionDetailsPage userType={UserTypes.Backoffice} />
            </Route>
            <Route
              exact
              path={`${FRONTOFFICE_SINGLE_TRANSACTION_ROUTE}/:transactionReference`}
            >
              <TransactionDetailsPage userType={UserTypes.Frontoffice} />
            </Route>
            <Route
              path="/front-office/payment-facilitator-disputes"
              component={FrontOfficeDisputes}
            />
            <Route
              path="/front-office/bank-disputes"
              component={FrontOfficeBankDisputes}
            />
            <Route
              path="/front-office/bulk-dispute-response"
              component={BulkDisputeResponse}
            />
            <Route
              path="/back-office/payment-facilitator-users"
              component={SuperAgentUsers}
            />
            <Route path="/back-office/fee-types" component={ChargeTypes} />
            <Route
              path="/back-office/settlement-participants"
              component={Participants}
            />
            <Route
              path="/back-office/public-holidays"
              component={PublicHolidays}
            />
            <Route
              path="/back-office/configurations"
              component={Configurations}
            />
            <Route path="/account/change-password" component={ChangePassword} />

            <Route
              path="/back-office/user-management"
              component={UserManagement}
            />
            <Route
              path="/back-office/participant-users"
              component={ParticipantUsers}
            />
            <Route
              path="/back-office/participant-reports"
              component={ParticipantReports}
            />
            <Route
              path="/back-office/workflow-requests/all"
              component={AllWorkflowRequests}
            />
            <Route
              path="/back-office/workflow-requests/self"
              component={SelfWorkflowRequests}
            />
            <Route
              path="/back-office/reconciliation/file-upload"
              component={UploadedReconciliationFiles}
            />
            <Route
              path="/back-office/reconciliation/daily-uploads"
              component={ReconciliationDailyUploads}
            />
            <Route
              path="/back-office/reconciliation/credit-reconciliation"
              component={CreditReconciliation}
            />
            <Route
              path="/back-office/reconciliation/debit-reconciliation"
              component={DebitReconciliation}
            />
            <Route
              path="/back-office/reconciliation/configurations"
              component={ReconciliationConfigurations}
            />
            <Route path="/back-office/bulk-disputes" component={BulkDisputes} />
            <Route
              path="/back-office/transit-accounts"
              component={TransitAccounts}
            />
            <Route
              path="/back-office/settings/verification-limits"
              component={VerificationLimits}
            />

            <Route
              path="/back-office/mandates"
              component={BackOfficeMandates}
            />
            <Route
              path="/back-office/mandates/:mandateReference"
              component={MandateDetails}
            />
            <Route
              path="/back-office/mandate-cancellation-requests"
              component={MandateCancellationRequests}
            />
            <Route
              path="/front-office/mandates"
              component={FrontOfficeMandates}
            />
            <Route
              path="/front-office/integration-details"
              component={FrontOfficeIntegrationDetails}
            />
            <Route
              path="/back-office/debit-mandate-configurations"
              component={DebitMandateConfigurations}
            />
            <Route
              path="/front-office/debit-mandate-configurations"
              component={FrontOfficeMandateConfigurations}
            />
            <Route
              path="/back-office/merchant-categories"
              component={MerchantCategories}
            />
            <Route path="**" component={PageNotFound} />
          </Switch>
        </div>
      </div>
    </div>
  );
};
